import type { CellIndex, Grid } from './types';
import { idx, idxCol, idxRow } from './types';
import { WELL_COLS, WELL_ROWS } from './types';

/**
 * Enchantment stones.
 *
 * Deliberately tiny first slice. Three stones, chosen to exercise three
 * different seams rather than to fill out a theme:
 *
 *   Shatter    — board interaction. Acts on the well before the resolve, so
 *                what it destroys can cascade.
 *   Resonance  — resolve/cascade hook. Reads the wave report after the fact.
 *   Echo       — card-zone manipulation. Moves a card between piles.
 *
 * Burn joins as the crafting slice's proof that the catalogue extends: a
 * landing-triggered stone whose damage scales with the piece's own Ember
 * count. Bleed remains wanted long-term and deliberately absent — it drags
 * in status persistence and enemy affinities this slice does not need.
 */
export type EnchantId = 'shatter' | 'resonance' | 'echo' | 'burn';

/**
 * What an enchantment responds to.
 *
 * Explicit because these will diverge. `match`, `row clear`, `cascade` and
 * `landing` are different events with different timing and different
 * deck-building implications, and future stones will care about them
 * separately. Collapsing them into one "did something happen" flag now would
 * mean unpicking it later across every hook.
 */
export type EnchantTrigger = 'landing' | 'match' | 'rowClear' | 'cascade';

export interface EnchantDef {
  readonly id: EnchantId;
  readonly name: string;
  readonly text: string;
  /**
   * Socket family. Nothing gates on it yet; it exists so compatibility rules
   * can be added later without reshaping the data.
   */
  readonly family: 'piece' | 'cascade' | 'utility';
  /** How many sockets a card needs before this can be fitted. */
  readonly tier: number;
  /** Which board events this stone answers. */
  readonly respondsTo: readonly EnchantTrigger[];
}

export const SHATTER: EnchantDef = {
  id: 'shatter',
  name: 'Shatter',
  text: 'On landing, destroy Stone orthogonally adjacent to the piece.',
  family: 'piece',
  tier: 1,
  respondsTo: ['landing'],
};

export const RESONANCE: EnchantDef = {
  id: 'resonance',
  name: 'Resonance',
  text: 'If this card causes a cascade of 2+, gain 2 Energy.',
  family: 'cascade',
  tier: 1,
  respondsTo: ['cascade'],
};

export const ECHO: EnchantDef = {
  id: 'echo',
  name: 'Echo',
  text: 'If this card matches or clears a row, shuffle it back into your draw pile.',
  family: 'utility',
  tier: 1,
  respondsTo: ['match', 'rowClear'],
};

export const BURN: EnchantDef = {
  id: 'burn',
  name: 'Burn',
  text: 'On landing, deal 2 damage per Ember gem in the piece.',
  family: 'piece',
  tier: 1,
  respondsTo: ['landing'],
};

export const ALL_ENCHANTS: readonly EnchantDef[] = [SHATTER, RESONANCE, ECHO, BURN];

/**
 * Sockets a single card instance can hold. The data model already supports
 * more — `enchants` is an array — but the prototype enforces one so a run
 * cannot trivially stack every stone onto one card.
 */
export const SOCKET_LIMIT = 1;

export function enchantById(id: EnchantId): EnchantDef | undefined {
  return ALL_ENCHANTS.find(e => e.id === id);
}

export function canSocket(enchants: readonly EnchantId[], capacity = SOCKET_LIMIT): boolean {
  return enchants.length < capacity;
}

/** Orthogonal neighbours of a cell that hold Stone. */
export function adjacentStone(grid: Grid, cells: readonly CellIndex[]): CellIndex[] {
  const found = new Set<CellIndex>();
  const dirs: readonly (readonly [number, number])[] = [[-1, 0], [1, 0], [0, -1], [0, 1]];
  for (const cell of cells) {
    const r = idxRow(cell);
    const c = idxCol(cell);
    for (const [dr, dc] of dirs) {
      const rr = r + dr;
      const cc = c + dc;
      if (rr < 0 || rr >= WELL_ROWS || cc < 0 || cc >= WELL_COLS) continue;
      const i = idx(rr, cc);
      if (grid[i] === 'stone') found.add(i);
    }
  }
  return [...found];
}

/** Clear Stone adjacent to the landed cells. Returns what it destroyed. */
export function shatterAdjacent(grid: Grid, cells: readonly CellIndex[]): CellIndex[] {
  const doomed = adjacentStone(grid, cells);
  for (const i of doomed) grid[i] = null;
  return doomed;
}

/** Deepest wave a resolve reached, or 0 when nothing cascaded. */
export function cascadeDepth(waves: readonly { wave: number }[]): number {
  let deepest = 0;
  for (const w of waves) if (w.wave > deepest) deepest = w.wave;
  return deepest;
}

/** Did this resolve create any match or row clear at all? */
export function createdMatch(waves: readonly { runs: readonly unknown[]; rowsCleared: readonly number[] }[]): boolean {
  return waves.some(w => w.runs.length > 0 || w.rowsCleared.length > 0);
}

/**
 * Does this resolve report the given trigger?
 *
 * `landing` is deliberately absent — it is not resolve-derived; it fires on
 * where a piece came to rest, which the caller knows and the wave report does
 * not describe.
 */
export function resolveReports(
  waves: readonly { wave: number; runs: readonly unknown[]; rowsCleared: readonly number[] }[],
  trigger: EnchantTrigger,
): boolean {
  switch (trigger) {
    case 'match':
      return waves.some(w => w.runs.length > 0);
    case 'rowClear':
      return waves.some(w => w.rowsCleared.length > 0);
    case 'cascade':
      return cascadeDepth(waves) >= 2;
    case 'landing':
      return false;
  }
}

/** Does this stone answer the given board event? */
export function respondsTo(def: EnchantDef, trigger: EnchantTrigger): boolean {
  return def.respondsTo.includes(trigger);
}
